/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { CarbonUser } from 'src/@types/carbonUser';
// @types
import { ActionMap } from 'src/@types/common';
import { IMerchant, MerchantContextType, MerchantState } from 'src/@types/merchant';
//
import { getMerchantByIdApi } from 'src/api/merchantApi';
import useAuth from 'src/hooks/useAuth';
import useCarbonUser from 'src/hooks/useCarbonUser';

// ----------------------------------------------------------------------

const initialState: MerchantState = {
  merchant: null,
  merchantUsers: [],
  isMerchantLoading: true,
};

enum Types {
  getMerchant = 'GET_MERCHANT',
  getMerchantUsers = 'GET_MERCHANT_USERS',
  isMerchantLoading = 'IS_MERCHANT_LOADING',
}

type MerchantPayload = {
  [Types.getMerchant]: {
    merchant: IMerchant | null;
  };
  [Types.getMerchantUsers]: {
    merchantUsers: IMerchant[] | null;
  };
  [Types.isMerchantLoading]: {
    isMerchantLoading: boolean;
  };
};

type MerchantActions = ActionMap<MerchantPayload>[keyof ActionMap<MerchantPayload>];

const reducer = (state: MerchantState, action: MerchantActions) => {
  if (action.type === 'GET_MERCHANT') {
    const { merchant } = action.payload;
    return {
      ...state,
      merchant,
    };
  }
  if (action.type === 'GET_MERCHANT_USERS') {
    const { merchantUsers } = action.payload;
    return {
      ...state,
      merchantUsers,
    };
  }
  if (action.type === 'IS_MERCHANT_LOADING') {
    const { isMerchantLoading } = action.payload;
    return {
      ...state,
      isMerchantLoading,
    };
  }
  return state;
};

const MerchantContext = createContext<MerchantContextType | null>(null);

// ----------------------------------------------------------------------

type MerchantProviderProps = {
  children: ReactNode;
};

function MerchantProvider({ children }: MerchantProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { carbonUser } = useCarbonUser();
  const { isAuthenticated } = useAuth();
  
  function getAdminRelationshipFromUser(user: CarbonUser, merchantId?: string) {
    const isStoredMerchantBelongsToUser = user?.merchantUsers?.find((user) => user?.merchantId === merchantId)

    if (isStoredMerchantBelongsToUser) {
      return isStoredMerchantBelongsToUser;
    }

    return user?.merchantUsers?.find((item) => item.role === 'ADMIN' || item.role === 'SUPER_USER') || null;
  }

  const getMerchantUsers = async () => {
    if (carbonUser?.merchantUsers) {
      const merchantIds = carbonUser?.merchantUsers?.map(
        (user: { merchantId: string; role: string }) => user?.merchantId
      );
      if (merchantIds) {
        const records = await Promise.all(
          merchantIds.map(async (record: string) => {
            try {
              const response = await getMerchantByIdApi(record, true);
              return response?.data;
            } catch (error) {
              console.log(error);
            }
          })
        );
        dispatch({
          type: Types.getMerchantUsers,
          payload: {
            ...state,
            merchantUsers: records,
          },
        });
      }
    }
  };

  const getMerchant = async (user: CarbonUser | null, id?: string) => { 
    if (user?.id && user?.merchantUsers?.length > 0) {
      const adminUser = getAdminRelationshipFromUser(user, id);

      if (adminUser?.merchantId) {
        try {
          const result = await getMerchantByIdApi(adminUser.merchantId, true);
          dispatch({
          type: Types.getMerchant,
          payload: {
            ...state,
            merchant: result.data,
          },
        });
        } catch (error) {
          // validate
        } finally {
          dispatch({
          type: Types.isMerchantLoading,
          payload: {
            ...state,
            isMerchantLoading: false,
          },
        });
        }
      }
    }
  };

  useEffect(() => {
    if(carbonUser && isAuthenticated) {
      const merchantFromLocalStorage = localStorage.getItem('storedMerchantId');
      let storedMerchantId = null;
      if(merchantFromLocalStorage) {
        try {
          storedMerchantId = JSON.parse(merchantFromLocalStorage);
        } catch (error) {
          console.error(error);
        }
      }
      getMerchant(carbonUser, storedMerchantId);
    } else {
      dispatch({
        type: Types.getMerchant,
        payload: {
          ...state,
          merchant: null,
        },
      });
    }
  }, [ carbonUser?.id, isAuthenticated ]);

  
  return (
    <MerchantContext.Provider
      value={{
        ...state,
        getMerchant,
        getMerchantUsers,
        getAdminRelationshipFromUser,
      }}
    >
      {children}
    </MerchantContext.Provider>
  );
}

export { MerchantContext, MerchantProvider };
